import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useOptionalAuth } from '@mono/ui-runtime/context';
import { useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../contexts/featureFlagContext';
import { artifactClient, govconClient } from '../../lib/connect/clients';
import { sameOriginStorageUrl } from '../../lib/storageUrl';
import { BlockOverlay } from '../ProposalDashboard/DocumentViewer/BlockOverlay';
import { toast } from '../toast';
import { useChatFeedback, useChatFeedbackAvailable, useChatReadOnly } from './ChatFeedbackContext';
import { useChatLinks } from './ChatLinksContext';
/**
 * A page of a stored PDF with the quoted words highlighted, fetched as the
 * signed-in user (the same renderer the citation viewer uses). A page that
 * cannot be read, or a quote that is not on it, says so; no position is
 * guessed.
 */ function ChatDocPage({ file, page, quote, caption }) {
    // The quote finds its own page: a model's page number is often the printed
    // one, not the PDF's. The given page is the fallback when the quote is not
    // on any page (a scan, a paraphrase).
    const q = useQuery({
        queryKey: [
            'chat-visual-page',
            file,
            page ?? 0,
            quote ?? ''
        ],
        queryFn: async ()=>{
            const located = await govconClient.getPricingSheetPage({
                sourceDocumentId: file,
                pageNumber: quote ? 0 : page ?? 1,
                quote: quote ?? ''
            });
            if (located.quoteMatched || !quote || !page || located.pageNumber === page) return located;
            return govconClient.getPricingSheetPage({
                sourceDocumentId: file,
                pageNumber: page,
                quote
            });
        },
        retry: false,
        staleTime: 5 * 60000
    });
    const heading = /*#__PURE__*/ _jsxs("div", {
        className: "mb-1.5 flex items-center gap-2 text-[length:var(--fs-ui-caption)] font-medium text-foreground",
        children: [
            /*#__PURE__*/ _jsx(FileText, {
                className: "h-4 w-4 text-muted-foreground",
                "aria-hidden": true
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "truncate",
                children: caption ?? 'Source page'
            }),
            q.data && /*#__PURE__*/ _jsxs("span", {
                className: "ml-auto shrink-0 text-xs font-normal text-muted-foreground",
                children: [
                    "Page ",
                    q.data.pageNumber,
                    q.data.totalPages ? ` of ${q.data.totalPages}` : ''
                ]
            })
        ]
    });
    if (q.isPending) {
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                heading,
                /*#__PURE__*/ _jsx("div", {
                    className: "h-64 w-full animate-pulse rounded-md bg-muted/50"
                })
            ]
        });
    }
    if (q.isError) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-md border border-border p-3",
            children: [
                heading,
                /*#__PURE__*/ _jsx("p", {
                    className: "text-xs text-muted-foreground",
                    children: "This page could not be opened."
                }),
                quote && /*#__PURE__*/ _jsx("blockquote", {
                    className: "mt-2 border-l-2 border-border pl-2 text-xs text-muted-foreground",
                    children: quote
                })
            ]
        });
    }
    const data = q.data;
    const blocks = data.blocks.filter((b)=>b.blockId.startsWith('citation-quote/')).map((b)=>({
            id: b.blockId,
            type: b.type,
            content: b.content,
            boundingBox: {
                x: b.x,
                y: b.y,
                width: b.width,
                height: b.height
            },
            confidence: 1
        }));
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            heading,
            /*#__PURE__*/ _jsxs("div", {
                className: "relative overflow-hidden rounded-md border border-border",
                children: [
                    /*#__PURE__*/ _jsx("img", {
                        className: "w-full",
                        src: data.imageDataUrl,
                        alt: caption ?? `Page ${data.pageNumber}`
                    }),
                    /*#__PURE__*/ _jsx(BlockOverlay, {
                        blocks: blocks,
                        pageDimensions: {
                            width: data.pageWidth,
                            height: data.pageHeight
                        },
                        selectedBlockId: null,
                        highlightedBlockIds: new Set(blocks.map((b)=>b.id)),
                        onBlockSelect: ()=>{}
                    })
                ]
            }),
            quote && !data.quoteMatched && /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-xs text-muted-foreground",
                children: "The quoted words were not found on this page; nothing is highlighted."
            })
        ]
    });
}
/** An image stored in the app, loaded through a signed same-origin URL. */ function ChatImage({ file, alt, caption }) {
    const q = useQuery({
        queryKey: [
            'chat-visual-image',
            file
        ],
        queryFn: async ()=>sameOriginStorageUrl((await artifactClient.generateDownloadUrl({
                artifactId: file
            })).url),
        retry: false,
        staleTime: 5 * 60000
    });
    if (q.isPending) return /*#__PURE__*/ _jsx("div", {
        className: "h-48 w-full animate-pulse rounded-md bg-muted/50"
    });
    if (q.isError || !q.data) {
        return /*#__PURE__*/ _jsxs("p", {
            className: "text-xs text-muted-foreground",
            children: [
                "The image ",
                caption ? `“${caption}” ` : '',
                "could not be opened."
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("figure", {
        className: "m-0",
        children: [
            /*#__PURE__*/ _jsx("img", {
                className: "max-h-96 w-full rounded-md border border-border object-contain",
                src: q.data,
                alt: alt
            }),
            caption && /*#__PURE__*/ _jsx("figcaption", {
                className: "mt-1 text-xs text-muted-foreground",
                children: caption
            })
        ]
    });
}
/** A file a chart reads is capped: the browser summarizes it, it does not hold a dataset. */ const MAX_CHART_FILE_BYTES = 20 * 1024 * 1024;
async function signedUrl(file) {
    return sameOriginStorageUrl((await artifactClient.generateDownloadUrl({
        artifactId: file
    })).url);
}
async function loadFile(file) {
    const res = await fetch(await signedUrl(file));
    if (!res.ok) throw new Error(`file ${file}: HTTP ${res.status}`);
    const size = Number(res.headers.get('content-length') ?? 0);
    if (size > MAX_CHART_FILE_BYTES) throw new Error('file too large to chart in the browser');
    return res.text();
}
/**
 * How the chat draws visuals whose content lives in the app, and runs the
 * next-step buttons a reply offers: ask sends the prompt into THIS chat (not
 * in a read-only shared one), open goes to an app page (beside the chat when
 * the surface can), download opens the file.
 */ export function useChatVisualHost() {
    const send = useChatFeedback();
    const canSend = useChatFeedbackAvailable();
    const readOnly = useChatReadOnly();
    const links = useChatLinks();
    // <Custom> code runs in its sandbox only where the flag is on, evaluated
    // for the viewer's tenant as shanks evaluates it.
    const tenantId = useOptionalAuth()?.user?.tenant_id;
    const customVisuals = useFeatureFlag('chat_custom_visuals', tenantId);
    return useMemo(()=>({
            DocPage: (props)=>/*#__PURE__*/ _jsx(ChatDocPage, {
                    ...props
                }),
            Image: (props)=>/*#__PURE__*/ _jsx(ChatImage, {
                    ...props
                }),
            loadFile,
            customVisuals,
            canAct: (a)=>a.kind === 'ask' ? canSend && !readOnly : true,
            onAction: (a)=>{
                if (a.kind === 'ask') {
                    send(a.prompt || a.label);
                } else if (a.kind === 'open' && a.href?.startsWith('/') && !a.href.startsWith('//')) {
                    if (links?.openInApp) links.openInApp(a.href);
                    else window.location.assign(a.href);
                } else if (a.kind === 'download' && a.file) {
                    signedUrl(a.file).then((url)=>window.open(url, '_blank', 'noopener,noreferrer')).catch(()=>toast({
                            type: 'error',
                            title: 'The file could not be opened'
                        }));
                }
            }
        }), [
        send,
        canSend,
        readOnly,
        links,
        customVisuals
    ]);
}
